import { zodResolver } from '@hookform/resolvers/zod'
import { NormalizedNumber, raise } from '@sparkdotfi/common-universal'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId } from 'wagmi'

import { useSpkFarmData } from '../../../data/useSpkFarmData'
import { calculateReward } from '../../../utils/calculateReward'
import { createTxOverview, TxOverview } from './createTxOverview'
import { getFormFieldsForUnstakeDialog } from './getFormFieldsForUnstakeDialog'
import { getUnstakeDialogFormValidator } from './validation'
import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { UnstakeObjective } from '@/features/actions/flavours/unstake/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import { useDebouncedFormValues } from '@/features/dialogs/common/logic/transfer-from-user/form'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { useSpkTokenRepository } from '@/features/spk/common/useSpkTokenRepository'
import { useTimestamp } from '@/utils/useTimestamp'

export interface UseUnstakeDialogResult {
  selectableAssets: TokenWithBalance[]
  assetsFields: FormFieldsForDialog
  form: UseFormReturn<AssetInputSchema>
  outcomeToken: TokenWithValue
  pageStatus: PageStatus
  txOverview: TxOverview
  exitFarmSwitchInfo: ExitFarmSwitchInfo
  actionsViewState: ActionsViewState
}

export interface ExitFarmSwitchInfo {
  showSwitch: boolean
  checked: boolean
  onSwitch: () => void
  reward: TokenWithValue
}

export function useUnstakeDialog(): UseUnstakeDialogResult {
  const chainId = useChainId()
  const { timestamp, updateTimestamp } = useTimestamp()
  useEffect(() => {
    updateTimestamp()
  }, [updateTimestamp])
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const { account } = useConnectedAddress()

  const tokenRepository = useSpkTokenRepository({ chainId, account })
  const spkFarmData = useSpkFarmData({ chainId, tokenRepository })

  const [exitFarmSwitchChecked, setExitFarmSwitchChecked] = useState(false)

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(
      getUnstakeDialogFormValidator({
        stakingToken: spkFarmData.stakingToken,
        staked: spkFarmData.staked,
        tokenRepository,
      }),
    ),
    defaultValues: {
      symbol: spkFarmData.stakingToken.symbol,
      value: '',
    },
    mode: 'onChange',
  })

  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedFormValues({
    form,
    tokenRepository,
  })

  const objectives: UnstakeObjective[] = [
    {
      type: 'unstake',
      farm: spkFarmData.farmAddress,
      stakingToken: spkFarmData.stakingToken,
      rewardToken: spkFarmData.rewardToken,
      amount: formValues.value,
      exit: exitFarmSwitchChecked,
    },
  ]

  const earnedRewards = calculateReward({
    earned: spkFarmData.earned,
    staked: spkFarmData.staked,
    rewardRate: spkFarmData.rewardRate,
    earnedTimestamp: spkFarmData.earnedTimestamp,
    periodFinish: spkFarmData.periodFinish,
    timestampInMs: timestamp * 1000,
    totalSupply: spkFarmData.totalSupply,
  })

  const txOverview = createTxOverview({
    spkFarmData,
    formValues,
    isExiting: exitFarmSwitchChecked,
    earnedRewards,
  })

  const outcomeTokenRouteItem =
    txOverview.status === 'success'
      ? (txOverview.routeToOutcomeToken.at(-1) ?? raise('Route should be defined'))
      : undefined

  const outcomeToken = {
    token: outcomeTokenRouteItem?.token ?? spkFarmData.stakingToken,
    value: outcomeTokenRouteItem?.value ?? NormalizedNumber.ZERO,
  }

  const actionsEnabled = formValues.value.gt(0) && isFormValid && !isDebouncing
  const canClaim = spkFarmData.earned.gt(0) || spkFarmData.rewardRate.gt(0)

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      tokenRepository,
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
  }, [
    formValues.token.symbol,
    formValues.value.toString(),
    formValues.isMaxSelected,
    exitFarmSwitchChecked,
    resetActions,
  ])

  return {
    selectableAssets: [{ token: spkFarmData.stakingToken, balance: spkFarmData.staked }],
    assetsFields: getFormFieldsForUnstakeDialog({
      form,
      tokenRepository,
      stakingToken: spkFarmData.stakingToken,
      staked: spkFarmData.staked,
    }),
    form,
    outcomeToken,
    txOverview,
    pageStatus: {
      state: pageStatus,
      actionsEnabled,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    exitFarmSwitchInfo: {
      showSwitch: formValues.isMaxSelected && canClaim,
      onSwitch: () => setExitFarmSwitchChecked((exitFarmSwitchChecked) => !exitFarmSwitchChecked),
      checked: exitFarmSwitchChecked,
      reward: {
        token: spkFarmData.rewardToken,
        value: earnedRewards,
      },
    },
    actionsViewState,
  }
}
